import { useEffect } from 'react';
import { buildTrack, locateOnTrack } from '../lib/nav';
import { useAppStore } from './store';

/**
 * Navigazione live: segue la posizione GPS mentre è attiva, la proietta sul
 * tracciato (quanto manca, ombra davanti, fuori percorso) e tiene lo schermo
 * acceso dove il browser lo consente.
 */
export function useNavigation(): void {
  const navActive = useAppStore((s) => s.navActive);
  const plan = useAppStore((s) => s.plan);

  useEffect(() => {
    if (!navActive) return;
    const { setHint, stopNav, setUserPos, setNavFix } = useAppStore.getState();
    if (!('geolocation' in navigator)) {
      setHint('Il browser non espone la geolocalizzazione: navigazione non disponibile.');
      stopNav();
      return;
    }
    const track = plan ? buildTrack(plan.chosen) : null;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const ll = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const accuracyM = pos.coords.accuracy;
        setUserPos({
          ll,
          accuracyM,
          heading: pos.coords.heading ?? null,
          at: pos.timestamp,
        });
        if (track) setNavFix(locateOnTrack(track, ll, accuracyM));
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setHint('Permesso di posizione negato: la navigazione ha bisogno del GPS.');
          stopNav();
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setHint('Posizione non disponibile al momento.');
        } else {
          setHint('Segnale GPS debole: attendo una posizione…');
        }
      },
      { enableHighAccuracy: true, maximumAge: 2_000, timeout: 20_000 },
    );

    // schermo acceso durante la navigazione (Wake Lock API, dove supportata)
    let lock: WakeLockSentinel | null = null;
    const requestLock = async () => {
      if (!('wakeLock' in navigator)) return;
      try {
        lock = await navigator.wakeLock.request('screen');
      } catch {
        lock = null;
      }
    };
    void requestLock();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void requestLock();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      document.removeEventListener('visibilitychange', onVisibility);
      void lock?.release().catch(() => undefined);
    };
  }, [navActive, plan]);
}
